export enum UserRole {
  BACKEND_ADMIN = 'BACKEND_ADMIN',
  DEALER_PRINCIPAL = 'DEALER_PRINCIPAL',
  INSURANCE_HEAD = 'INSURANCE_HEAD',
  TEAM_LEADER = 'TEAM_LEADER',
  TELECALLER = 'TELECALLER',
}

export enum LeadStatus {
  NEW = 'NEW',
  WHATSAPP_QUALIFYING = 'WHATSAPP_QUALIFYING',
  QUALIFIED = 'QUALIFIED',
  CALLBACK_REQUESTED = 'CALLBACK_REQUESTED',
  QUOTATION_SENT = 'QUOTATION_SENT',
  IN_NEGOTIATION = 'IN_NEGOTIATION',
  POLICY_ISSUED = 'POLICY_ISSUED',
  LOST = 'LOST',
  LOST_RENEWED_ELSEWHERE = 'LOST_RENEWED_ELSEWHERE',
  NOT_RESPONSIVE = 'NOT_RESPONSIVE',
  DUPLICATE = 'DUPLICATE',
  STALE = 'STALE',
}

export enum LeadSource {
  NEW_VEHICLE_SALE = 'NEW_VEHICLE_SALE',
  RENEWAL = 'RENEWAL',
  BODY_SHOP = 'BODY_SHOP',
  WALK_IN = 'WALK_IN',
  OEM_DUMP = 'OEM_DUMP',
  API = 'API',
  BODY_MANAGEMENT = 'BODY_MANAGEMENT',
  OTHER = 'OTHER',
}

export enum CallDisposition {
  CALLBACK_REQUESTED = 'CALLBACK_REQUESTED',
  NOT_INTERESTED = 'NOT_INTERESTED',
  ASKING_FOR_DISCOUNT = 'ASKING_FOR_DISCOUNT',
  CUSTOMER_BOOKED_ELSEWHERE = 'CUSTOMER_BOOKED_ELSEWHERE',
  SOLD_VEHICLE = 'SOLD_VEHICLE',
  DUE_DATE_LAPSED = 'DUE_DATE_LAPSED',
  IN_PROCESS = 'IN_PROCESS',
  POLICY_ISSUED = 'POLICY_ISSUED',
  NO_ANSWER = 'NO_ANSWER',
  WRONG_NUMBER = 'WRONG_NUMBER',
}

export interface User {
  id: number
  tenant_id: number
  email: string
  mobile: string
  full_name: string
  role: UserRole
  is_active: boolean
  language: string
  last_login_at: string | null
  created_at: string
  tenant_feature_flags?: Record<string, unknown>
}

export interface UserCreate {
  email: string
  mobile: string
  full_name: string
  password: string
  role: UserRole
  language?: string
  tenant_id?: number
}

export enum CallStatus {
  INITIATED   = 'INITIATED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED   = 'COMPLETED',
  NO_ANSWER   = 'NO_ANSWER',
  BUSY        = 'BUSY',
  FAILED      = 'FAILED',
}

export enum CallSource {
  EXOTEL         = 'EXOTEL',
  MANUAL_MOBILE  = 'MANUAL_MOBILE',
  MANUAL_DESKTOP = 'MANUAL_DESKTOP',
}

export const TERMINAL_CALL_STATUSES = new Set([
  CallStatus.COMPLETED,
  CallStatus.NO_ANSWER,
  CallStatus.BUSY,
  CallStatus.FAILED,
])

export interface Lead {
  id: number
  tenant_id: number
  customer_name: string
  mobile: string
  vehicle_reg_no: string | null
  vehicle_chassis_no: string | null
  vehicle_model: string | null
  insurance_due_date: string | null
  lead_source: LeadSource
  status: LeadStatus
  priority_score: number
  priority_reason: string | null
  assigned_to: number | null
  assigned_to_name: string | null
  last_contacted_at: string | null
  language_preference: string
  customer_notes: string | null
  dnd_status: string | null
  extra_data: Record<string, unknown> | null
  policy_number: string | null
  policy_issued_at: string | null
  customer_id: number | null
  vehicle_id: number | null
  created_at: string
  updated_at?: string | null
  status_changed_at?: string | null
  last_wa_summary?: string | null
  had_claim_last_year?: boolean | null
}

export interface PreviousPolicy {
  lead_id: number
  policy_number: string | null
  insurance_company: string | null
  premium_amount: number | null
  od_discount_percentage: number | null
  policy_issued_at: string | null
  insurance_due_date: string | null
}

export interface CallStatusResponse {
  call_log_id: number
  call_status: CallStatus
  duration_seconds: number | null
}

export interface LeadCreate {
  customer_name: string
  mobile: string
  vehicle_reg_no?: string
  vehicle_chassis_no?: string
  vehicle_model?: string
  insurance_due_date?: string
  lead_source: LeadSource
  language_preference?: string
  customer_notes?: string
  whatsapp_opted_in?: boolean
  dedup_mode?: 'skip' | 'update' | 'create'
}

export interface LeadListResponse {
  items: Lead[]
  total: number
  page: number
  page_size: number
}

export interface CallLog {
  id: number
  lead_id: number
  called_by: number
  caller_name?: string
  call_sid?: string
  status?: string
  source?: CallSource
  disposition: CallDisposition | null
  remarks: string | null
  duration_seconds: number | null
  od_discount_offered: number | null
  requires_approval: boolean
  approved_by?: number | null
  approved_at?: string | null
  callback_at?: string | null
  exotel_payload?: Record<string, string> | null
  transcript?: string | null
  ai_analysis?: CallAnalysis | null
  analysis?: CallAnalysis | null
  recording_url?: string | null
  recording_md5?: string | null
  call_direction?: 'outbound' | 'inbound' | null
  call_duration_s?: number | null
  created_at: string
}

export interface CallAnalysis {
  language?: string
  duration_seconds?: number | null
  customer_sentiment?: 'positive' | 'neutral' | 'negative'
  customer_intent?: string
  key_topics?: string[]
  agent_score?: {
    greeting: number
    product_knowledge: number
    objection_handling: number
    closing: number
    overall: number
  }
  coaching_notes?: string
  training_flags?: string[]
  follow_up_action?: string
  parse_error?: string
  raw?: string
}

export interface CallInitiateResponse {
  call_log_id: number
  call_sid: string
  status: string
  within_requested_slot: boolean | null
}

export interface DashboardSummary {
  total_leads: number
  new_leads_today: number
  calls_made_today: number
  policies_issued_today: number
  total_revenue_today: number
  active_telecallers: number
  open_fraud_alerts: number
}

export interface TelecallerPerformance {
  user_id: number
  name: string
  calls_today: number
  total_calls: number
  policies_issued: number
  conversion_rate: number
}

export interface WorkloadItem {
  user_id: number
  name: string
  open_queue_depth: number
  total_calls: number
  conversions: number
  conversion_rate: number
  last_active: string | null
  avg_call_duration_seconds: number | null
  sla_breached_count: number
  allocation_weight: number
  load_health: 'overloaded' | 'balanced' | 'underloaded'
}

export interface TelecallerWeight {
  user_id: number
  name: string
  language: string
  allocation_weight: number
  open_queue_depth: number
}

export interface ReallocationSuggestion {
  lead_id: number
  vehicle_reg_no: string | null
  vehicle_model: string | null
  priority_score: number
  status: LeadStatus
  reason: string
  reason_code: 'SLA_BREACH' | 'LOAD_IMBALANCE'
  current_tc_id: number
  current_tc_name: string
  current_tc_queue_depth: number
  suggested_tc_id: number
  suggested_tc_name: string
  suggested_tc_queue_depth: number
  hours_since_last_contact: number | null
}

export interface AuthTokens {
  access_token: string
  refresh_token: string
  token_type: string
  user: User
}

export interface TimelineEvent {
  timestamp: string
  event_type: 'CALL' | 'MESSAGE' | 'STATUS_CHANGE' | 'DOCUMENT' | 'REMINDER' | 'SMS' | 'EMAIL' | 'INGESTION'
  description: string
  details: Record<string, unknown> | null
}

export interface TimelineResponse {
  events: TimelineEvent[]
}

export interface BulkUploadResult {
  batch_id: number
  total: number
  success: number
  failures: Array<{ row: number; error: string }>
  warnings?: string[]
}

export interface ExistingLeadSummary {
  id: number
  customer_name: string
  mobile: string
  vehicle_reg_no: string | null
  status: string
  assigned_to: number | null
  insurance_due_date: string | null
}

export interface DuplicateMatch {
  row_index: number
  incoming_name: string | null
  incoming_mobile: string
  incoming_vehicle: string | null
  existing: ExistingLeadSummary
}

export interface DuplicateCheckResponse {
  duplicates: DuplicateMatch[]
  unique_count: number
  total: number
}

export interface MappingTemplate {
  id: number
  tenant_id: number
  name: string
  mapping: Record<string, string>
  created_at: string
}

export interface Quote {
  id: number
  lead_id: number
  insurer_name: string | null
  policy_type: string | null
  premium_amount: number | null
  idv: number | null
  od_premium: number | null
  tp_premium: number | null
  zero_depreciation: boolean
  roadside_assistance: boolean
  engine_protection: boolean
  consumables_cover: boolean
  ncb_protection: boolean
  key_replacement: boolean
  status: 'DRAFT' | 'SENT' | 'SELECTED' | 'REJECTED'
  pdf_filename: string | null
  created_at: string
}

export interface ExtractionConfidence {
  insurer_name: number
  policy_type: number
  premium_amount: number
  idv: number
  od_premium: number
  tp_premium: number
  zero_depreciation: number
  roadside_assistance: number
  engine_protection: number
  consumables_cover: number
  ncb_protection: number
  key_replacement: number
}

export interface QuoteExtractionResult {
  quote_id: number
  insurer_name: string | null
  policy_type: string | null
  premium_amount: number | null
  idv: number | null
  od_premium: number | null
  tp_premium: number | null
  zero_depreciation: boolean
  roadside_assistance: boolean
  engine_protection: boolean
  consumables_cover: boolean
  ncb_protection: boolean
  key_replacement: boolean
  confidence: ExtractionConfidence
}

export interface QuotePreviewMessage {
  message: string
  quote_count: number
}

export interface FraudAlert {
  id: number
  tenant_id: number
  alert_type: string
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
  status: 'OPEN' | 'INVESTIGATING' | 'RESOLVED' | 'FALSE_POSITIVE'
  evidence: Record<string, unknown> | null
  description: string | null
  resolved_by?: number | null
  resolved_at?: string | null
  resolution_note?: string | null
  created_at: string
}

export interface FraudAlertStats {
  total: number
  by_status: {
    open: number
    investigating: number
    resolved: number
    false_positive: number
  }
  by_severity: Record<string, number>
}

export interface CashReconciliation {
  id: number
  tenant_id: number
  reconciliation_date: string
  total_cash_collected: number
  total_cash_deposited: number
  variance: number
  is_resolved: boolean
  deposited_by?: number | null
  variance_explanation?: string | null
  created_at: string
}

export interface Reminder {
  id: number
  lead_id: number
  assigned_to: number
  reminder_at: string
  note: string | null
  is_completed: boolean
  source: 'MANUAL' | 'SYSTEM_PROMPTED' | 'CUSTOMER_REQUESTED'
  slot_start: string | null
  slot_end: string | null
  created_at: string
}

export interface PolicyResponse {
  lead_id: number
  policy_number: string
  insurance_company: string
  premium_amount: number
  od_discount_percentage: number
  payment_mode: string
  payment_reference: string
  policy_issued_at: string
  policy_issued_by: number
  policy_verification_status: string | null
}

export interface RenewalPipelineLead {
  id: number
  customer_name: string
  mobile: string
  vehicle_reg_no: string | null
  insurance_due_date: string
  policy_number: string | null
}

export interface WhatsAppTemplate {
  id: number
  name: string
  category: 'utility' | 'marketing' | 'authentication'
  language: string
  body: string | null
  header: string | null
  footer: string | null
  button_definition: Record<string, unknown> | null
  variable_mapping: Record<string, string> | null
  approval_status: 'pending' | 'approved' | 'rejected'
  rejection_reason: string | null
  is_active: boolean
  version: number
  created_at: string
}

export interface WhatsAppMessage {
  id: number
  direction: 'INBOUND' | 'OUTBOUND'
  template_name: string | null
  body: string | null
  status: string
  button_reply: string | null
  created_at: string
  media_url?: string | null
  extra_data?: {
    delivery_errors?: { code: number; title: string; details: string }[]
    send_error?: { http_status: number; code?: number; message?: string; error_data?: string }
    [key: string]: unknown
  } | null
}

export interface BillingService {
  id: number
  name: string
  slug: string
  service_type: string
  description: string | null
  is_active: boolean
  created_at: string
}

export interface Subscription {
  id: number
  tenant_id: number
  service_id: number
  service_slug: string
  service_name: string
  service_type: string
  status: 'active' | 'suspended' | 'cancelled' | 'expired'
  pricing_tier: string | null
  billing_cycle: string | null
  price_per_cycle: number | null
  next_billing_at: string | null
  started_at: string
  expires_at: string | null
  created_at: string
}

export interface ActiveFeatures {
  tenant_id: number
  active_slugs: string[]
  has_core: boolean
}

export interface TierPricing {
  monthly: number
  annual: number
  description: string | null
}

export interface ServicePricing {
  slug: string
  tiers: Record<string, TierPricing>
}

export interface PricingCatalog {
  version: string
  currency: string
  services: Record<string, ServicePricing>
}

export interface DiscountApprovalItem {
  id: number
  lead_id: number
  called_by: number
  caller_name?: string
  od_discount_offered: number
  requires_approval: boolean
  approved_by?: number | null
  approved_at?: string | null
  remarks?: string | null
  created_at: string
}

// ── Customer entity ───────────────────────────────────────────────────────────

export interface Vehicle {
  id: number
  customer_id: number
  tenant_id: number
  registration_number: string
  make: string | null
  model: string | null
  year: number | null
  fuel_type: string | null
  created_at: string
  updated_at: string
}

export interface CustomerLeadSummary {
  id: number
  status: string
  lead_source: string
  vehicle_reg_no: string | null
  vehicle_model: string | null
  insurance_due_date: string | null
  priority_score: number
  premium_amount: number | null
  policy_number: string | null
  assigned_to_name: string | null
  created_at: string
}

export interface Customer {
  id: number
  tenant_id: number
  name: string
  mobile: string
  email: string | null
  city: string | null
  is_active: boolean
  merged_into_id: number | null
  created_at: string
  updated_at: string
}

export interface CustomerDetail extends Customer {
  vehicles: Vehicle[]
  leads: CustomerLeadSummary[]
  total_leads: number
  renewals_completed: number
  lifetime_premium: number
  years_as_customer: number
  last_contacted_at: string | null
}

export interface CustomerListResponse {
  items: Customer[]
  total: number
  page: number
  page_size: number
}

export interface NameConflict {
  customer_id: number
  existing_name: string
  incoming_name: string
  mobile: string
}

export interface NameConflictResponse {
  conflict: NameConflict
  hint: string
}

export interface MergeRequest {
  source_customer_id: number
}

export interface MergeResponse {
  canonical_id: number
  tombstoned_id: number
  leads_repointed: number
  vehicles_repointed: number
}

export interface SplitRequest {
  new_customer_name: string
  new_customer_mobile?: string
  lead_ids: number[]
  vehicle_ids: number[]
}

export interface SplitResponse {
  original_customer_id: number
  new_customer_id: number
  leads_moved: number
  vehicles_moved: number
}
